import api, { API_ENDPOINTS } from '@/utils/api';
import { User, CreateUserResponse, UpdateUserResponse } from '@/types/users';

// ----------------------------------------------------------------------
//
// Compatibility shim: the v2 backend returns User payloads in camelCase
// (Prisma defaults), but the legacy PWA components were written against
// the snake_case Xano shape. Rather than rewrite every component, we
// normalize at the service boundary so consumers keep reading
// `user.first_name`, `user.picture_profile?.url`, etc.
//
// Drop this shim once each component has been migrated to camelCase.
//
// ----------------------------------------------------------------------

type CamelUser = {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  mobileCountryCode?: string | null;
  mobileNumber?: string | null;
  role?: string;
  enabled?: boolean;
  emailVerified?: boolean;
  hikvisionPersonId?: string | null;
  onboarderId?: string | null;
  defaultMarketId?: string | null;
  currencyId?: string | null;
  languageId?: string | null;
  newsletter?: boolean;
  notifications?: boolean;
  pictureProfileKey?: string | null;
  pictureProfileUrl?: string | null;
  createdAt?: string;
  updatedAt?: string;
};

const PUBLIC_S3_BASE =
  process.env.NEXT_PUBLIC_PUBLIC_S3_BASE_URL || 'https://im-s3.numenai.tech';
const PUBLIC_BUCKET =
  process.env.NEXT_PUBLIC_PUBLIC_S3_BUCKET || 'intellimarket-public';

function s3PublicUrl(key: string | null | undefined): string | null {
  if (!key) return null;
  return `${PUBLIC_S3_BASE}/${PUBLIC_BUCKET}/${key}`;
}

export function normalizeUser(raw: CamelUser): User {
  return {
    id: Number(raw.id),
    email: raw.email,
    first_name: raw.firstName ?? '',
    last_name: raw.lastName ?? '',
    mobile_country_code: raw.mobileCountryCode ?? '',
    mobile_number: raw.mobileNumber ?? null,
    role: (raw.role ?? 'CUSTOMER').toLowerCase() as User['role'],
    enabled: raw.enabled ?? true,
    onboarder_id: raw.onboarderId !== null && raw.onboarderId !== undefined
      ? Number(raw.onboarderId)
      : (null as unknown as number),
    default_market_id:
      raw.defaultMarketId !== null && raw.defaultMarketId !== undefined
        ? Number(raw.defaultMarketId)
        : (null as unknown as number),
    currency_id:
      raw.currencyId !== null && raw.currencyId !== undefined
        ? Number(raw.currencyId)
        : (null as unknown as number),
    language_id:
      raw.languageId !== null && raw.languageId !== undefined
        ? Number(raw.languageId)
        : (null as unknown as number),
    // Prefer the backend-supplied public URL (it owns the bucket layout);
    // fall back to building one from the key for resilience / older payloads.
    picture_profile:
      raw.pictureProfileUrl || raw.pictureProfileKey
        ? ({
            url: (raw.pictureProfileUrl ??
              s3PublicUrl(raw.pictureProfileKey)) as string,
            path: raw.pictureProfileKey ?? '',
            name: (raw.pictureProfileKey ?? raw.pictureProfileUrl ?? '')
              .split('/')
              .pop() ?? '',
            access: 'public',
            type: 'image',
            size: 0,
            mime: 'image/*',
            meta: {},
          } as User['picture_profile'])
        : null,
    // Backend doesn't expose the legacy Hikvision face image object
    // any more — just the personId. The PWA only checks
    // hikvision_face_entrance?.url for "did the user complete face
    // registration?", so we synthesise a truthy stub when the user
    // has a hikvisionPersonId.
    hikvision_face_entrance: raw.hikvisionPersonId
      ? ({
          url: raw.hikvisionPersonId,
          path: '',
          name: '',
          access: 'private',
          type: 'image',
          size: 0,
          mime: 'image/*',
          meta: {},
        } as User['hikvision_face_entrance'])
      : null,
    // Catalog joins aren't returned anymore — populate stubs so
    // legacy components don't crash on user.currency.symbol etc.
    currency: { id: 0, created_at: 0, name: '', symbol: '$' } as User['currency'],
    language: { id: 0, created_at: 0, name: '', code: 'es' } as User['language'],
    // Communication prefs now come from the backend (toPublic / auth me).
    newsletter: raw.newsletter ?? false,
    notifications: raw.notifications ?? true,
    deleted: false,
    birth_date: '',
    id_number: '',
    password: '',
    date_of_birth: '',
    doc_country: '',
    doc_number: '',
    push_notification_token_id: (null as unknown as number),
    terms_and_condition: true,
    privacy_policy: true,
    accepted_tc_date: null,
    accepted_pp_date: null,
    created_at: 0,
  };
}

// ----------------------------------------------------------------------

// Public signup. Backend enforces the new-user side effects (Hikvision
// person creation, Scanntech client provisioning, welcome email) via
// the saga in UsersService — the customer just POSTs.
export const postUsers = (newUser: Partial<User>): Promise<CreateUserResponse> =>
  api.post('/users', { params: newUser });

// `image_profile` is a base64-encoded data URL. Backend posts to
// /users/:id/picture (Xano flat path /users/upload/picture is gone).
// We use /auth/me first to know who we're updating — the legacy form
// piggy-backed on a header the new backend doesn't honour.
export const updateProfilePicture = async (
  image_profile: string
): Promise<UpdateUserResponse> => {
  const me = await api.get(API_ENDPOINTS.auth.me);
  const updated = await api.post(`/users/${me.id}/picture`, {
    params: { image: image_profile },
  });
  return normalizeUser(updated);
};

// Request-side counterpart to `normalizeUser`: the legacy PWA builds patch
// objects in snake_case (Xano shape), but the NestJS UpdateUserDto is
// camelCase with `whitelist + forbidNonWhitelisted`, so any snake_case key
// (or a field the DTO doesn't accept, like `email`/`doc_number`) makes the
// whole PATCH 400. We map the known editable fields and drop the rest.
const USER_PATCH_FIELD_MAP: Record<string, string> = {
  first_name: 'firstName',
  last_name: 'lastName',
  mobile_country_code: 'mobileCountryCode',
  mobile_number: 'mobileNumber',
  newsletter: 'newsletter',
  notifications: 'notifications',
  default_market_id: 'defaultMarketId',
  currency_id: 'currencyId',
  language_id: 'languageId',
  enabled: 'enabled',
};

// FK ids the backend reads with `BigInt(value)` — send them as strings
// (or null to clear).
const USER_PATCH_ID_FIELDS = new Set(['defaultMarketId', 'currencyId', 'languageId']);

export function denormalizeUserPatch(
  patch: Partial<User> & Record<string, unknown>
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [snake, camel] of Object.entries(USER_PATCH_FIELD_MAP)) {
    const value = patch[snake];
    if (value === undefined) continue;
    out[camel] = USER_PATCH_ID_FIELDS.has(camel)
      ? value === null || value === ''
        ? null
        : String(value)
      : value;
  }
  return out;
}

export const updateUsers = async (
  id: number,
  updateUser: Partial<User>
): Promise<UpdateUserResponse> => {
  const updated = await api.patch(`/users/${id}`, {
    params: denormalizeUserPatch(updateUser as Partial<User> & Record<string, unknown>),
    next: { revalidate: 0 },
  });
  return normalizeUser(updated);
};

export const getUser = async (id: number): Promise<User> => {
  const raw = await api.get(`/users/${id}`, { next: { revalidate: 0 } });
  return normalizeUser(raw);
};
